import {
  DesktopEnvironmentBootstrapEnvelope,
  type DesktopEnvironmentBootstrapEnvelope as DesktopEnvironmentBootstrapEnvelopeType,
} from "@cmmd-forge/contracts";
import { isLoopbackUrl } from "@cmmd-forge/shared/loopback";
import { Schema } from "effect";

const LOCAL_WEB_BOOTSTRAP_PATH = "/api/dev/local-web/bootstrap";

// Several independent callers (root route beforeLoad, the environment
// descriptor retry loop, the environment connection service) each call
// hydrateRuntimeBridgeBootstrapEnvelope() unconditionally on their own
// schedule, expecting it to be cheap once resolved. A confirmed-absent
// result (204/404, or a network failure) can't be cached forever — a
// desktop dev shell may still register the bridge moments after the page
// loads — but it must be cached for *some* window, or a caller that re-runs
// in a tight loop (e.g. a navigation cascade re-invoking beforeLoad) turns
// into hundreds of requests per second.
const NEGATIVE_HYDRATION_COOLDOWN_MS = 3000;

let cachedBootstrapEnvelope: DesktopEnvironmentBootstrapEnvelopeType | null = null;
let hydrationPromise: Promise<DesktopEnvironmentBootstrapEnvelopeType | null> | null = null;
let lastNegativeHydrationAt: number | null = null;

function withinNegativeHydrationCooldown(): boolean {
  return (
    lastNegativeHydrationAt !== null &&
    Date.now() - lastNegativeHydrationAt < NEGATIVE_HYDRATION_COOLDOWN_MS
  );
}

function decodeBootstrapEnvelope(value: unknown): DesktopEnvironmentBootstrapEnvelopeType | null {
  try {
    return Schema.decodeUnknownSync(DesktopEnvironmentBootstrapEnvelope)(value);
  } catch {
    return null;
  }
}

function canHydrateLocalWebBridge(): boolean {
  if (typeof window === "undefined" || !import.meta.env.DEV) {
    return false;
  }
  return (window.location.port ?? "").length > 0 && isLoopbackUrl(window.location.origin);
}

export function readRuntimeBridgeBootstrapEnvelope(): DesktopEnvironmentBootstrapEnvelopeType | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window.desktopBridge?.getLocalEnvironmentBootstrap() ?? cachedBootstrapEnvelope;
}

async function fetchLocalWebBridgeBootstrap(): Promise<DesktopEnvironmentBootstrapEnvelopeType | null> {
  if (!canHydrateLocalWebBridge() || window.desktopBridge) {
    cachedBootstrapEnvelope = null;
    return readRuntimeBridgeBootstrapEnvelope();
  }

  // Network failures (proxy not ready, Vite restart, brief API flap) must not
  // throw into root beforeLoad — that becomes `Error in route match: __root__/`.
  let response: Response;
  try {
    response = await fetch(new URL(LOCAL_WEB_BOOTSTRAP_PATH, window.location.origin), {
      credentials: "include",
    });
  } catch {
    cachedBootstrapEnvelope = null;
    lastNegativeHydrationAt = Date.now();
    return null;
  }
  if (response.status === 204 || response.status === 404) {
    cachedBootstrapEnvelope = null;
    lastNegativeHydrationAt = Date.now();
    return null;
  }
  if (!response.ok) {
    cachedBootstrapEnvelope = null;
    lastNegativeHydrationAt = Date.now();
    return null;
  }

  const decoded = decodeBootstrapEnvelope(await response.json().catch(() => null));
  cachedBootstrapEnvelope = decoded;
  lastNegativeHydrationAt = null;
  return decoded;
}

export async function hydrateRuntimeBridgeBootstrapEnvelope(): Promise<DesktopEnvironmentBootstrapEnvelopeType | null> {
  const desktopEnvelope =
    typeof window === "undefined" ? null : window.desktopBridge?.getLocalEnvironmentBootstrap();
  if (desktopEnvelope) {
    cachedBootstrapEnvelope = null;
    return desktopEnvelope;
  }
  if (cachedBootstrapEnvelope) {
    return cachedBootstrapEnvelope;
  }
  if (withinNegativeHydrationCooldown()) {
    return null;
  }

  if (hydrationPromise) {
    return hydrationPromise;
  }

  hydrationPromise = fetchLocalWebBridgeBootstrap().finally(() => {
    hydrationPromise = null;
  });
  return hydrationPromise;
}

export function setRuntimeBridgeBootstrapEnvelopeForTests(
  envelope: DesktopEnvironmentBootstrapEnvelopeType | null,
): void {
  cachedBootstrapEnvelope = envelope;
}

export function __resetRuntimeBridgeForTests(): void {
  cachedBootstrapEnvelope = null;
  hydrationPromise = null;
  lastNegativeHydrationAt = null;
}
